import type { Metafield } from "@estelle/domain";

/**
 * How a mirrored metafield reads on the product detail (#308, FR-CAT-5).
 *
 * Here rather than in the screen so the two questions it answers — what to
 * call the field, and what to show for it — have one answer wherever a
 * metafield is drawn.
 */

/** `watch-display` and `rating_count` as "Watch display" and "Rating count". */
export function metafieldLabel(field: Metafield): string {
  const words = field.key.replace(/[-_]+/g, " ").trim();
  return words === ""
    ? field.key
    : words.charAt(0).toUpperCase() + words.slice(1);
}

export interface MetafieldText {
  text: string;
  /**
   * False when `text` is the verbatim value of a reference the channel would
   * not resolve — a list of gids. Still shown, because it is what the store
   * holds, but a screen should not dress it as an answer.
   */
  readable: boolean;
}

/** `{"scale_max":"5.0","value":"4.8"}` as "4.8 out of 5", or null. */
function ratingText(value: string): string | null {
  try {
    const parsed = JSON.parse(value) as { value?: string; scale_max?: string };
    const rating = Number(parsed.value);
    const max = Number(parsed.scale_max);
    if (!Number.isFinite(rating) || !Number.isFinite(max)) return null;
    return `${String(rating)} out of ${String(max)}`;
  } catch {
    return null;
  }
}

/**
 * What to show for a metafield: its display value where the API has one,
 * otherwise its value.
 *
 * `displayValue` is the names behind a reference — "Analog", "Brass, Gold".
 * It is null when the value already reads as itself, and also when the
 * channel would not resolve the references; in the second case the value is
 * a list of gids, which is shown as it is stored and flagged unreadable.
 *
 * A `rating` is the one type formatted here: its value is a JSON object, and
 * the three numbers in it are the whole meaning.
 */
export function metafieldText(field: Metafield): MetafieldText {
  if (field.displayValue !== null && field.displayValue !== "") {
    return { text: field.displayValue, readable: true };
  }
  if (field.type === "rating") {
    const text = ratingText(field.value);
    if (text !== null) return { text, readable: true };
  }
  return {
    text: field.value,
    readable: !field.type.includes("reference"),
  };
}
